import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Flappy.css';

const DEFAULT_ROUND_MS = 60_000;
const TRAP_CHANCE = 0.15;

const COLORS = [
  { key: 'blue', label: 'Blue', hex: '#3b82f6' },
  { key: 'green', label: 'Green', hex: '#10b981' },
  { key: 'yellow', label: 'Yellow', hex: '#facc15' },
  { key: 'orange', label: 'Orange', hex: '#f97316' },
  { key: 'white', label: 'White', hex: '#ffffff' },
  { key: 'pink', label: 'Pink', hex: '#ec4899' },
  { key: 'purple', label: 'Purple', hex: '#a855f7' },
  { key: 'red', label: 'Red', hex: '#ef4444' }
];

const COLOR_BY_KEY = Object.fromEntries(COLORS.map((c) => [c.key, c]));
const ALL_COLOR_KEYS = COLORS.map((c) => c.key);

function buildOptionKeys(correctKey) {
  const pool = ALL_COLOR_KEYS.filter((k) => k !== correctKey);
  const decoys = shuffleArray(pool).slice(0, 3);
  const options = [correctKey, ...decoys].filter(Boolean);
  // Guarantee exactly 4 unique keys where possible.
  const unique = Array.from(new Set(options));
  while (unique.length < 4 && pool.length) {
    const next = pool.find((k) => !unique.includes(k));
    if (!next) break;
    unique.push(next);
  }
  const shuffled = shuffleArray(unique).slice(0, 4);
  if (correctKey && !shuffled.includes(correctKey)) {
    shuffled[shuffled.length - 1] = correctKey;
  }
  return shuffled;
}

function pickDifferentIndex(n, notIndex) {
  if (n <= 1) return 0;
  let idx = Math.floor(Math.random() * n);
  while (idx === notIndex) idx = Math.floor(Math.random() * n);
  return idx;
}

function getMultiplier(streak) {
  if (streak >= 9) return 4;
  if (streak >= 6) return 3;
  if (streak >= 3) return 2;
  return 1;
}

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function ReactionSolo() {
  const navigate = useNavigate();

  const [status, setStatus] = useState('ready'); // ready | running | done
  const [timeLeftMs, setTimeLeftMs] = useState(DEFAULT_ROUND_MS);

  const [promptWordIdx, setPromptWordIdx] = useState(0);
  const [promptInkIdx, setPromptInkIdx] = useState(1);
  const [correctKey, setCorrectKey] = useState(null);
  const [optionKeys, setOptionKeys] = useState([]);

  const [score, setScore] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [wrong, setWrong] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [isTrap, setIsTrap] = useState(false);
  const [locked, setLocked] = useState(false);
  const [lastResult, setLastResult] = useState(null); // 'correct' | 'wrong' | null

  const intervalRef = useRef(null);
  const endAtRef = useRef(null);

  const attempts = correct + wrong;
  const accuracy = attempts > 0 ? correct / attempts : 0;
  const accuracyBonus = attempts >= 5 && accuracy >= 0.9 ? Math.round(accuracy * 20) : 0;
  const multiplier = getMultiplier(streak);

  const subtitle = useMemo(() => {
    const s = Math.ceil(timeLeftMs / 1000);
    if (status === 'ready') return 'Practice • 60 seconds • Click the INK color (not the word)';
    if (status === 'running') return `Time left: ${s}s • Score: ${score} • ✅ ${correct} / ❌ ${wrong} • Streak x${multiplier}`;
    if (status === 'done') return `Done • Score: ${score + accuracyBonus} • ✅ ${correct} / ❌ ${wrong}`;
    return '';
  }, [status, timeLeftMs, score, correct, wrong, multiplier, accuracyBonus]);

  const cleanupInterval = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
  };

  const nextPrompt = () => {
    const wordIdx = Math.floor(Math.random() * COLORS.length);
    const inkIdx = pickDifferentIndex(COLORS.length, wordIdx);
    setPromptWordIdx(wordIdx);
    setPromptInkIdx(inkIdx);

    const trap = Math.random() < TRAP_CHANCE;
    setIsTrap(trap);

    let nextCorrectKey = trap ? COLORS[wordIdx]?.key : COLORS[inkIdx]?.key;
    if (!nextCorrectKey) {
      nextCorrectKey = ALL_COLOR_KEYS[Math.floor(Math.random() * ALL_COLOR_KEYS.length)];
    }
    setCorrectKey(nextCorrectKey);
    setOptionKeys(buildOptionKeys(nextCorrectKey));
  };

  useEffect(() => () => cleanupInterval(), []);

  const start = () => {
    cleanupInterval();
    setStatus('running');
    setScore(0);
    setCorrect(0);
    setWrong(0);
    setStreak(0);
    setBestStreak(0);
    setIsTrap(false);
    setLocked(false);
    setLastResult(null);
    setTimeLeftMs(DEFAULT_ROUND_MS);

    endAtRef.current = Date.now() + DEFAULT_ROUND_MS;
    nextPrompt();

    intervalRef.current = setInterval(() => {
      const left = Math.max(0, (endAtRef.current ?? Date.now()) - Date.now());
      setTimeLeftMs(left);
      if (left <= 0) {
        cleanupInterval();
        setLocked(true);
        setStatus('done');
      }
    }, 120);
  };

  const handleAnswer = (choiceKey) => {
    if (status !== 'running') return;
    if (locked) return;
    if (timeLeftMs <= 0) return;

    setLocked(true);
    const expectedKey = correctKey || (isTrap ? COLORS[promptWordIdx]?.key : COLORS[promptInkIdx]?.key);
    const isCorrect = choiceKey === expectedKey;
    setLastResult(isCorrect ? 'correct' : 'wrong');
    if (isCorrect) {
      const nextStreak = streak + 1;
      const mult = getMultiplier(nextStreak);
      setScore((s) => s + (2 * mult));
      setCorrect((c) => c + 1);
      setStreak(nextStreak);
      setBestStreak((b) => (nextStreak > b ? nextStreak : b));
    } else {
      setScore((s) => s - 3);
      setWrong((w) => w + 1);
      setStreak(0);
    }

    window.setTimeout(() => {
      setLastResult(null);
      nextPrompt();
      setLocked(false);
    }, 140);
  };

  const reset = () => {
    cleanupInterval();
    endAtRef.current = null;
    setStatus('ready');
    setTimeLeftMs(DEFAULT_ROUND_MS);
    setScore(0);
    setCorrect(0);
    setWrong(0);
    setStreak(0);
    setBestStreak(0);
    setIsTrap(false);
    setLocked(false);
    setLastResult(null);
    setCorrectKey(null);
    setCorrectKey(null);
    setOptionKeys([]);
  };

  useEffect(() => {
    if (!correctKey) return;
    if (!optionKeys.length || !optionKeys.includes(correctKey)) {
      setOptionKeys(buildOptionKeys(correctKey));
    }
  }, [correctKey, optionKeys]);

  const promptWord = COLORS[promptWordIdx]?.label ?? '—';
  const promptInk = COLORS[promptInkIdx]?.hex ?? '#ffffff';
  const promptInkKey = COLORS[promptInkIdx]?.key;

  return (
    <div className="flappy-page">
      <div className="flappy-header">
        <div>
          <h1 className="flappy-title">🎨 Color Confusion (Practice)</h1>
          <div className="flappy-subtitle">{subtitle}</div>
        </div>
        <div className="flappy-header-actions">
          <button className="flappy-btn" onClick={() => navigate('/dashboard')}>← Dashboard</button>
          <button className="flappy-btn" onClick={() => navigate('/reaction')}>Modes</button>
        </div>
      </div>

      <div className="flappy-grid">
        <section className="flappy-card">
          <h2>How it works</h2>
          <div className="flappy-meta">
            <div><strong>Step 1:</strong> Press Start (60 seconds)</div>
            <div><strong>Step 2:</strong> Look at the word and its INK color</div>
            <div><strong>Step 3:</strong> Click the INK color from the 4 options</div>
          </div>
          <div className="flappy-hint">Traps flip the rule. Streaks boost points. High accuracy adds a bonus.</div>
        </section>

        <section className="flappy-card">
          <h2>Play</h2>

          <div style={{
            borderRadius: 14,
            border: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(255,255,255,0.03)',
            padding: 18,
            minHeight: 160,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}>
            {status === 'running' ? (
              <div>
                <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.72)', marginBottom: 10 }}>
                  Click the <strong>INK COLOR</strong>
                </div>
                {isTrap && (
                  <div style={{ fontSize: 12, color: '#f97316', marginBottom: 8, fontWeight: 700 }}>
                    Trap: click the WORD color
                  </div>
                )}
                <div
                  style={{
                    fontSize: 44,
                    fontWeight: 900,
                    letterSpacing: 2,
                    textTransform: 'uppercase',
                    color: promptInk,
                    textShadow:
                      promptInkKey === 'white'
                        ? '0 2px 0 rgba(0,0,0,0.85), 0 10px 30px rgba(0,0,0,0.45)'
                        : '0 8px 30px rgba(0,0,0,0.35)'
                  }}
                >
                  {promptWord}
                </div>
                <div style={{ marginTop: 10, fontSize: 12, color: 'rgba(255,255,255,0.72)' }}>
                  Example: the word may say “Pink” but the ink is Blue.
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: 'rgba(255,255,255,0.92)' }}>
                  {status === 'done' ? 'Time!' : 'Ready'}
                </div>
                <div style={{ marginTop: 8, color: 'rgba(255,255,255,0.72)', fontSize: 13 }}>
                  {status === 'done' ? 'Press Start to play again.' : 'Press Start to begin the 60-second round.'}
                </div>
              </div>
            )}
          </div>

          <div
            style={{
              marginTop: 12,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 10,
            }}
          >
            {status === 'running' ? (
              (optionKeys.length === 4 ? optionKeys : Array.from({ length: 4 }).map(() => null)).map((key, idx) => {
                const c = key ? COLOR_BY_KEY[key] : null;
                if (!c) {
                  return (
                    <button
                      key={`missing-${idx}`}
                      className="flappy-btn"
                      disabled
                      style={{
                        padding: '14px 12px',
                        borderRadius: 12,
                        border: '1px solid rgba(255,255,255,0.10)',
                        background: 'rgba(255,255,255,0.02)',
                        color: 'transparent',
                      }}
                    >
                      &nbsp;
                    </button>
                  );
                }

                return (
                  <button
                    key={c.key}
                    className={lastResult === 'correct' ? 'flappy-btn-primary' : 'flappy-btn'}
                    onClick={() => handleAnswer(c.key)}
                    disabled={locked || optionKeys.length !== 4}
                    style={{
                      padding: '14px 12px',
                      borderRadius: 12,
                      border: '1px solid rgba(255,255,255,0.10)',
                      background:
                        lastResult === 'wrong'
                          ? 'linear-gradient(135deg, rgba(239,68,68,0.22), rgba(239,68,68,0.10))'
                          : (lastResult === 'correct'
                            ? 'linear-gradient(135deg, rgba(16,185,129,0.22), rgba(16,185,129,0.10))'
                            : 'rgba(255,255,255,0.03)'),
                    }}
                  >
                    {c.label}
                  </button>
                );
              })
            ) : (
              Array.from({ length: 4 }).map((_, idx) => (
                <button
                  key={`blank-${idx}`}
                  className="flappy-btn"
                  disabled
                  style={{
                    padding: '14px 12px',
                    borderRadius: 12,
                    border: '1px solid rgba(255,255,255,0.10)',
                    background: 'rgba(255,255,255,0.02)',
                    color: 'transparent',
                  }}
                >
                  &nbsp;
                </button>
              ))
            )}
          </div>

          <div style={{ marginTop: 12, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button className="flappy-btn-primary" onClick={start} disabled={status === 'running'}>Start</button>
            <button className="flappy-btn" onClick={reset}>Reset</button>
          </div>

          {status === 'done' && (
            <div className="flappy-hint">
              Final: <strong>Score {score + accuracyBonus}</strong> • ✅ {correct} • ❌ {wrong} • Accuracy {(accuracy * 100).toFixed(1)}% • Bonus +{accuracyBonus} • Best streak {bestStreak}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
